import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material';

import { SessionService } from './session.service';
import { DBConnectionComponentService } from './db-connection-component.service';
import { DynamicComponent } from '@app/shared/components/dynamic-component/dynamic-component.component';

import {
    AddExceptionDialogComponent,
    AddExceptionDialogData,

    AddFromListDialogComponent,
    AddFromListDialogData,

    // AddOneFromListDialogComponent,
    // AddOneFromListDialogData,

    AddTagDialogComponent,
    AddTagDialogData,

    SuccessDialogComponent,
    SuccessDialogData,

    ErrorDialogComponent,
    ErrorDialogData,

    MultiUpgradePreValidatePackageDialogComponent,
    MultiUpgradePreValidatePackageDialogData,

    MultiRollbackPackageDialogComponent,
    MultiRollbackPackageDialogData,

    PackagePromotionDialogData,
    PackagePromotionDialogComponent,

    ConfirmationDialogComponent,
    ConfirmationDialogData,

    RegexpCheckerDialogComponent,
    RegexpCheckerDialogData,

    ScriptViewerDialogComponent,
    ScriptViewerDialogData,

    TimeoutDialogComponent,
    TimeoutDialogData,

    ContainerDialogComponent,
    ContainerDialogData,

    CustomContainerDialogData
} from '@app/shared/components/dialogs';
import { VaultConnectionService } from '@src/services/VaultConnectionService.service';
import { ChatbotConnectionService } from '@src/services/ChatbotConnectionService.service';


const AUTO_CLOSE_TIME = 2000;
const LONGER_AUTO_CLOSE_TIME = 10000;

@Injectable({ providedIn: 'root' })
export class DialogManagerService {
    private isDialogOpened = false;

    constructor(
        private dialog: MatDialog,
        private sessionService: SessionService,
        private dbConnectionComponentService: DBConnectionComponentService,
        private vaultConnectionService: VaultConnectionService,
        private chatbotConnectionService: ChatbotConnectionService) {
    }

    public showCustomDialog(data: CustomContainerDialogData, onConfirm = (_: any) => {}, panelClass?: string) {
        const dialogRef = this.dialog.open(ContainerDialogComponent, { data, panelClass });
        this.setClosing(dialogRef, (result: any) => result && onConfirm(result));
    }

    public showDBConnectionDialog(html: string, data: ContainerDialogData, onConfirm = (_: any) => {}) {
        const connectionComponent = this.dbConnectionComponentService.createComponent(html);
        this.buildDynamicDialog(connectionComponent, data, onConfirm);
    }

    public showVaultDialog(html: string, data: ContainerDialogData, onConfirm = (_: any) => {}) {
        const connectionComponent = this.vaultConnectionService.createComponent(html);
        this.buildDynamicDialog(connectionComponent, data, onConfirm);
    }

    public showChatbotDialog(html: string, data: ContainerDialogData, onConfirm = (_: any) => {}) {
        const connectionComponent = this.chatbotConnectionService.createComponent(html);
        this.buildDynamicDialog(connectionComponent, data, onConfirm);
    }

    private buildDynamicDialog(connectionComponent, data: ContainerDialogData, onConfirm = (_: any) => {}) {
        const setData = (instance: any) => {
            instance.component = connectionComponent;
            instance.compile = true;
            instance.setData = data.setData;
        };
        const customData = { ...data, setData, component: DynamicComponent };
        const panelClass = 'custom-modal-settings';
        const dialogRef = this.dialog.open(ContainerDialogComponent, { panelClass, data: customData });
        this.setClosing(dialogRef, (result: any) => result && onConfirm(result));
    }

    public showSuccessDialog(data: SuccessDialogData, onClose = () => {}) {
        if (!this.isDialogOpened || data.secondDialog) {
            const dialogRef = this.dialog.open(SuccessDialogComponent, { data });
            let timer;
            if (data.title === 'Publish Project' || data.title === 'Update Project Variables') {
                timer = setTimeout(() => dialogRef.close(), LONGER_AUTO_CLOSE_TIME);
            } else {
                timer = setTimeout(() => dialogRef.close(), AUTO_CLOSE_TIME);
            }
            this.setClosing(dialogRef, () => {
                clearTimeout(timer);
                onClose();
            });
        }
    }

    public showErrorDialog(data: ErrorDialogData, onClose = () => {}) {
        if (!this.isDialogOpened || data.secondDialog) {
            const dialogRef = this.dialog.open(ErrorDialogComponent, { data });
            this.setClosing(dialogRef, onClose);
        }
    }

    public showGlobalErrorDialog(data: ErrorDialogData, onClose = () => { }) {
        const dialogRef = this.dialog.open(ErrorDialogComponent, { data });
        this.setClosing(dialogRef, onClose);
    }

    public showConfirmationDialog(data: ConfirmationDialogData, onConfirm = () => {}) {

        const dialogRef = this.dialog.open(ConfirmationDialogComponent,{ data , panelClass: 'confirmation-window-adjust' });
        this.setClosing(dialogRef, (confirmed: boolean) => confirmed && onConfirm());
    }

    public showRegexpCheckerDialog(data: RegexpCheckerDialogData, onEdit = (_: RegexpCheckerDialogData) => {}) {
        const dialogRef = this.dialog.open(RegexpCheckerDialogComponent, { data, panelClass: 'modal-lg' });
        this.setClosing(dialogRef, (result: RegexpCheckerDialogData) => result && onEdit(result));
    }

    public showScriptViewerDialog(data: ScriptViewerDialogData, onEdit = () => {}) {
        const userToken = this.sessionService.user.access_token;
        const rawScript = typeof data.script === 'string' ? data.script : (data.script != null ? JSON.stringify(data.script) : '');
        const script = rawScript.replace(/"/g, '&quot;');
        const dialogRef = this.dialog.open(ScriptViewerDialogComponent, { data: { ...data, userToken, script } });
        this.setClosing(dialogRef, (edited: boolean) => edited && onEdit());
    }

    public showTimeoutDialog(data: TimeoutDialogData, onConfirm = () => {}) {
        const dialogRef = this.dialog.open(TimeoutDialogComponent, { data });
        this.setClosing(dialogRef, (confirmed: boolean) => confirmed && onConfirm());
    }

    public showAddExceptionDialog(
        data: AddExceptionDialogData,
        onConfirm = (_: AddExceptionDialogData) => {}) {
        const dialogRef = this.dialog.open(AddExceptionDialogComponent, { data });
        this.setClosing(dialogRef, (result: AddExceptionDialogData) => result && onConfirm(result));
    }

    public showAddFromListDialog(data: AddFromListDialogData, onConfirm = (_: AddFromListDialogData) => {}) {
        const dialogRef = this.dialog.open(AddFromListDialogComponent, { data });
        this.setClosing(dialogRef, (result: AddFromListDialogData) => result && onConfirm(result));
    }

    // public showAddOneFromListDialog(data: AddOneFromListDialogData, onConfirm = (_: AddOneFromListDialogData) =>{}) {
    //     const dialogRef = this.dialog.open(AddOneFromListDialogComponent, { data });
    //     this.setClosing(dialogRef, (result: AddOneFromListDialogData) => result && onConfirm(result));
    // }

    public showAddTagDialog(data: AddTagDialogData, onConfirm = (_: AddTagDialogData) => {}) {
        const dialogRef = this.dialog.open(AddTagDialogComponent, { data });
        this.setClosing(dialogRef, (result: AddTagDialogData) => result && onConfirm(result));
    }

    public showMultiRollbackPackageDialog(data: MultiRollbackPackageDialogData,
                                          onConfirm = (_: MultiRollbackPackageDialogData) => {}) {
        const dialogRef = this.dialog.open(MultiRollbackPackageDialogComponent, { data });
        this.setClosing(dialogRef, (result: MultiRollbackPackageDialogData) => result && onConfirm(result));
    }

    public showMultiUpgradePreVaildatePackageDialog(data: MultiUpgradePreValidatePackageDialogData) {
        const dialogRef = this.dialog.open(MultiUpgradePreValidatePackageDialogComponent, { data });
        this.setClosing(dialogRef, () => {});
    }

    public showPackagePromotionDialog(data: PackagePromotionDialogData,
                                      onConfirm = (_: PackagePromotionDialogData) => {}) {
        const dialogRef = this.dialog.open(PackagePromotionDialogComponent, { data });
        this.setClosing(dialogRef, (result: PackagePromotionDialogData) => result && onConfirm(result));
    }

    public showDialog<T>(data: T, onConfirm = (_: T) => {}, component, panelClass?: string) {
        const dialogRef = this.dialog.open(component, { data, panelClass });
        this.setClosing(dialogRef, (result: T) => result && onConfirm(result));
    }

    private setClosing<TDialog>(dialogRef: MatDialogRef<TDialog, any>, onClose: (value: any) => void) {
        this.isDialogOpened = true;
        const subscription = dialogRef.afterClosed().subscribe((value: any) => {
            subscription.unsubscribe();
            this.isDialogOpened = false;
            onClose(value);
        });
    }

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/services/dialog-manager.service.ts